<template>
  <v-dialog v-model="show" max-width="450" persistent overlay-color="black" overlay-opacity="0.8" transition="dialog-bottom-transition">
    <v-card dark class="rounded-xl elevation-24" style="border: 1px solid rgba(255, 255, 255, 0.1)">
      <v-progress-linear v-if="loading || apiLoading" indeterminate color="primary accent-2" height="3" absolute top></v-progress-linear>

      <v-btn icon small absolute top right class="mt-3 mr-3" @click="closeDialog" v-if="!loading">
        <v-icon color="grey darken-2">mdi-close</v-icon>
      </v-btn>

      <v-card-text class="text-center px-6 py-10">
        <v-window v-model="step" touchless>
          <v-window-item :value="1" class="py-2">
            <v-avatar color="grey darken-3" size="90" class="mb-6 elevation-4">
              <v-icon color="primary lighten-2" x-large>mdi-shield-check</v-icon>
            </v-avatar>

            <h2 class="text-h4 font-weight-bold mb-3 white--text">Identität prüfen</h2>
            <p class="text-body-1 grey--text text--lighten-1 mb-8">
              Um fortzufahren, müssen wir kurz bestätigen, dass <b>{{ email }}</b> dir gehört.
            </p>

            <v-btn
              block
              x-large
              color="primary"
              class="rounded-lg text-none font-weight-bold black--text"
              @click="startVerification"
              elevation="0"
              height="56">
              Code senden
            </v-btn>

            <div class="mt-4">
              <v-btn text x-small color="grey darken-1" @click="closeDialog">Später erinnern</v-btn>
            </div>
          </v-window-item>

          <v-window-item :value="2" class="py-2">
            <div class="text-overline primary--text text--lighten-2 mb-1">E-Mail gesendet</div>
            <h3 class="text-h5 font-weight-bold mb-8 white--text">Code eingeben</h3>

            <div class="d-flex justify-center mb-8">
              <v-progress-circular
                :rotate="-90"
                :size="110"
                :width="5"
                :value="(timeRemaining / timerDuration) * 100"
                :color="timerColor"
                class="transition-swing">
                <div class="d-flex flex-column align-center">
                  <span class="text-h4 font-weight-black white--text" style="line-height: 1">{{ formattedTime }}</span>
                  <span class="text-caption grey--text text--lighten-1">min</span>
                </div>
              </v-progress-circular>
            </div>

            <div class="mb-6" style="min-height: 80px">
              <v-otp-input
                v-if="hasOtpSupport"
                v-model="verificationCode"
                length="6"
                type="number"
                class="justify-center dark-otp"
                dark
                @finish="confirmCode"></v-otp-input>

              <v-text-field
                v-else
                v-model="verificationCode"
                placeholder="• • • • • •"
                outlined
                dark
                class="centered-input text-h4 rounded-lg font-weight-bold"
                maxlength="6"
                height="64"
                type="tel"
                hide-details
                @paste="handlePaste"
                @keyup.enter="confirmCode"></v-text-field>
            </div>

            <v-btn
              :loading="apiLoading"
              :disabled="verificationCode.length !== 6"
              color="primary"
              x-large
              block
              class="rounded-lg text-none font-weight-bold black--text mb-4"
              @click="confirmCode"
              elevation="0"
              height="56">
              Bestätigen
            </v-btn>

            <v-fade-transition>
              <v-btn v-if="showResendButton" text small color="grey lighten-1" class="text-none" @click="startVerification">
                Code erneut senden
              </v-btn>
              <span v-else class="text-caption grey--text text--darken-2"> Neuer Code in {{ resendInSeconds }}s </span>
            </v-fade-transition>
          </v-window-item>

          <v-window-item :value="3" class="py-4">
            <v-avatar color="grey darken-3" size="90" class="mb-6">
              <v-icon color="error lighten-1" x-large>mdi-timer-off-outline</v-icon>
            </v-avatar>
            <h3 class="text-h5 font-weight-bold mb-3 white--text">Abgelaufen</h3>
            <p class="text-body-1 grey--text text--lighten-1 mb-6">Der Code ist nicht mehr gültig.</p>
            <v-btn
              color="primary"
              x-large
              block
              class="rounded-lg text-none font-weight-bold black--text"
              @click="startVerification"
              elevation="0"
              height="56">
              Neuer Versuch
            </v-btn>
          </v-window-item>

          <v-window-item :value="4" class="py-4">
            <v-avatar color="grey darken-3" size="90" class="mb-6">
              <v-icon color="success lighten-1" x-large>mdi-check-decagram</v-icon>
            </v-avatar>
            <h3 class="text-h4 font-weight-bold mb-3 white--text">Perfekt!</h3>
            <p class="text-body-1 grey--text text--lighten-1">Deine E-Mail wurde erfolgreich bestätigt.</p>
            <v-progress-linear indeterminate color="success" class="mt-6 rounded" rounded></v-progress-linear>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  // Das v-model im Parent steuert, ob der Dialog offen ist
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 1,
      verified: false,
      email: "",
      loading: true, // Initiale Ladezeit
      apiLoading: false, // Button Ladezeit

      // Timer Logic
      verificationStartDate: new Date(),
      timerDuration: 900,
      timeRemaining: 900,
      interval: null,
      resendDelay: 60,

      verificationCode: "",
      hasOtpSupport: true,
    }
  },
  computed: {
    // Getter/Setter für v-model Support des Dialogs
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
    formattedTime() {
      const minutes = Math.floor(this.timeRemaining / 60)
      const seconds = ("0" + (this.timeRemaining % 60)).slice(-2)
      return `${minutes}:${seconds}`
    },
    timerColor() {
      if (this.timeRemaining < 60) return "error lighten-1"
      if (this.timeRemaining < 300) return "warning lighten-1"
      return "primary lighten-1"
    },
    showResendButton() {
      return this.timeRemaining <= this.timerDuration - this.resendDelay
    },
    resendInSeconds() {
      return Math.ceil(this.timeRemaining - (this.timerDuration - this.resendDelay))
    },
  },
  created() {
    this.hasOtpSupport = !!this.$options.components?.VOtpInput || false
    // Wir laden den Status sofort, wenn die Komponente erstellt wird (auch wenn Dialog noch zu ist)
    this.fetchVerifiedStatus()
  },
  watch: {
    // Optional: Wenn der Dialog geöffnet wird, Status nochmal prüfen oder Timer fixen
    value(val) {
      if (val && !this.verified) {
        // z.B. Logik wenn Dialog aufgeht
      }
    },
  },
  beforeDestroy() {
    this.stopCountdown()
  },
  methods: {
    closeDialog() {
      this.show = false
    },
    handlePaste(event) {
      event.preventDefault()
      const pasteData = (event.clipboardData || window.clipboardData).getData("text")
      const cleanedCode = pasteData.replace(/\D/g, "").slice(0, 6)
      if (cleanedCode) {
        this.verificationCode = cleanedCode
        if (cleanedCode.length === 6) {
          this.$nextTick(() => {
            this.confirmCode()
          })
        }
      }
    },
    stopCountdown() {
      if (this.interval) clearInterval(this.interval)
    },
    startCountdown() {
      this.stopCountdown()
      this.verificationStartDate = new Date()
      this.timeRemaining = this.timerDuration
      this.interval = setInterval(() => {
        const now = new Date()
        const diff = Math.floor((now - this.verificationStartDate) / 1000)
        this.timeRemaining = this.timerDuration - diff
        if (this.timeRemaining <= 0) {
          this.stopCountdown()
          this.step = 3
        }
      }, 1000)
    },
    async startVerification() {
      if (this.apiLoading) return
      this.apiLoading = true
      this.verificationCode = ""
      try {
        let status = await api.startEmailVerification()
        if (status.created) {
          this.$toast.success("Code gesendet")
          this.step = 2
          this.startCountdown()
        } else {
          this.$toast.error("Fehler beim Senden.")
        }
      } catch (e) {
        this.$toast.error("Verbindungsfehler.")
      } finally {
        this.apiLoading = false
      }
    },
    async confirmCode() {
      if (this.apiLoading || !this.verificationCode || this.verificationCode.length !== 6) return
      this.apiLoading = true
      try {
        let status = await api.verifyEmailVerificationCode({ code: this.verificationCode })
        if (status.verified) {
          this.stopCountdown()
          this.step = 4
          this.$emit("emailVerified")
          // Dialog nach 2 Sekunden automatisch schliessen
          setTimeout(() => {
            this.show = false
          }, 2000)
        } else {
          this.$toast.error("Code ungültig.")
          this.verificationCode = ""
        }
      } catch (e) {
        this.$toast.error("Fehler bei der Überprüfung.")
      } finally {
        this.apiLoading = false
      }
    },
    async fetchVerifiedStatus() {
      this.loading = true
      try {
        let status = await api.fetchUserEmailVerificationStatus()
        this.verified = status.verified
        this.email = status.email
        if (this.verified) {
          this.$emit("emailVerified")
          // Wenn schon verifiziert, vielleicht gar nicht erst Dialog-Logic starten?
          // this.step = 4
        } else {
          this.$emit("emailNotVerified")
          this.step = 1
        }
      } catch (e) {
        // Silent fail oder Log
        console.error(e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* Clean Input Style for Fallback */
.centered-input >>> input {
  text-align: center;
  letter-spacing: 0.5rem;
  color: #fff !important;
}

/* Smooth Animation */
.transition-swing {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);
}
</style>
